import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";

/**
 * Books — "Library Showcase"
 * Large featured cover on the left + 2×2 matrix of supporting covers on the
 * right, with a long-form excerpt panel beneath.
 */

const FEATURED = {
  image:
    "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/pruzqzgh_a3-anchor-awaken-advance-cover.png",
  title:
    "a³: Anchor, Awaken, Advance — Reclaiming the Soul of the Muslim Educator",
  status: "Featured Title",
};

const MATRIX = [
  {
    image:
      "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/gf1huxcl_thewayofthemurabbicover.png",
    title: "The Way of the Murabbi",
  },
  {
    image:
      "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/hsaq4ab7_mindelevationscover.png",
    title: "Mind Elevations",
  },
  {
    image:
      "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/4zqpcwsy_man%20prayer%20psalm%20of%20gnosis.jpg",
    title: "Psalms of Gnosis",
  },
  {
    image:
      "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/23y5gy80_seashoresofmymindcover.png",
    title: "The Seashores of My Mind",
  },
];

/* ── Cover tile ─────────────────────────────────────────────────────── */
const CoverTile = ({ book, testId, fillHeight = false }) => (
  <div
    data-testid={testId}
    className={`group relative overflow-hidden border border-white/10 hover:border-azure/40 transition-colors duration-700 bg-ink ${
      fillHeight ? "h-full" : "aspect-[3/4]"
    }`}
    style={{
      boxShadow: "0 30px 60px -20px rgba(0,0,0,0.55)",
    }}
  >
    <img
      src={book.image}
      alt={book.title}
      className="absolute inset-0 w-full h-full object-contain transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
    />

    {/* Cinematic ambient sheen */}
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(circle 320px at 20% 15%, rgba(201,169,97,0.08), transparent 60%), radial-gradient(circle 360px at 85% 95%, rgba(74,143,229,0.10), transparent 70%)",
        mixBlendMode: "screen",
      }}
    />

    {/* Inner azure frame */}
    <div className="absolute inset-3 border border-white/10 pointer-events-none" />

    {/* Hover micro-glow */}
    <div
      aria-hidden="true"
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
      style={{
        background:
          "radial-gradient(circle 320px at 50% 50%, rgba(74,143,229,0.12), transparent 70%)",
      }}
    />
  </div>
);

/* ── Section ────────────────────────────────────────────────────────── */
const BooksSection = ({ section }) => {
  return (
    <section
      data-testid={`featured-section-${section.id}`}
      className="relative overflow-hidden bg-ink-deep"
    >
      {/* Ambient library light */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 50% 50% at 25% 30%, rgba(201,169,97,0.08), transparent 60%), radial-gradient(ellipse 55% 60% at 80% 90%, rgba(74,143,229,0.10), transparent 65%)",
        }}
      />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-16 lg:py-22">
        {/* Eyebrow + headline */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-10 lg:mb-14">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4">
              <span className="text-[11px] tracking-[0.34em] uppercase text-azure-soft">
                {section.eyebrow}
              </span>
              <span className="h-px w-10 bg-azure-soft/50" />
            </div>
            <h2 className="mt-9 font-serif text-white text-[32px] sm:text-[44px] lg:text-[52px] leading-[1.02] tracking-[-0.02em]">
              {section.headlineA}
            </h2>
            <h2 className="font-serif italic text-azure-soft text-[32px] sm:text-[44px] lg:text-[52px] leading-[1.02] tracking-[-0.02em]">
              {section.headlineB}
            </h2>
          </div>
          <div className="lg:col-span-4 lg:pl-10 lg:border-l lg:border-white/10">
            <p className="text-white/65 text-[14.5px] leading-[1.95] max-w-md">
              {section.intro}
            </p>
          </div>
        </div>

        {/* Showcase: featured (tall) + 2×2 matrix + excerpt — single row, equal heights */}
        <div className="grid lg:grid-cols-12 gap-5 lg:gap-6 items-stretch">
          {/* Featured — single column, full height */}
          <div className="lg:col-span-2">
            <Link
              to={section.viewAllHref}
              data-testid={`featured-card-${section.id}-main`}
              className="block h-full"
            >
              <CoverTile book={FEATURED} testId="book-cover-featured" fillHeight />
            </Link>
          </div>

          {/* 2×2 matrix */}
          <div className="lg:col-span-4 grid grid-cols-2 gap-5 lg:gap-6 self-stretch">
            {MATRIX.map((b, i) => (
              <Link
                key={b.title}
                to={section.viewAllHref}
                data-testid={`featured-card-${section.id}-${i}`}
                className="block"
              >
                <CoverTile book={b} testId={`book-cover-${i}`} />
              </Link>
            ))}
          </div>

          {/* Excerpt panel — full height */}
          <div className="lg:col-span-6">
            <div className="relative h-full p-6 lg:p-8 border border-white/10 bg-ink/40 overflow-hidden flex flex-col">
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse 60% 80% at 100% 0%, rgba(74,143,229,0.10), transparent 70%), radial-gradient(ellipse 50% 60% at 0% 100%, rgba(201,169,97,0.06), transparent 70%)",
                }}
              />
              {/* Decorative oversized quote glyph */}
              <span
                aria-hidden="true"
                className="absolute -top-2 left-5 font-serif text-azure-soft/30 text-[120px] leading-[0.8] select-none pointer-events-none"
              >
                “
              </span>

              <div className="relative flex flex-col h-full">
                <div className="text-[10.5px] tracking-[0.34em] uppercase text-azure-soft">
                  Excerpt
                </div>
                <h4 className="mt-5 font-serif text-white text-[19px] lg:text-[24px] leading-[1.18] tracking-[-0.015em]">
                  Adab Is Caught,{" "}
                  <span className="italic text-azure-soft">Not Taught.</span>
                </h4>

                <p className="mt-6 font-serif text-white/85 text-[15px] lg:text-[16.5px] leading-[1.85] tracking-[-0.003em]">
                  Students don't just learn from your lessons — they learn from
                  your tone, your face, your reactions, your habits. Believe me.
                  Every little quirk you think goes unnoticed is very much
                  noticed. If you cut off students mid-sentence, they learn
                  impatience. If you mock them when they're wrong, they learn
                  fear and arrogance. If you welcome mistakes with calm and
                  kindness, they learn patience and resilience.
                </p>
                <p className="mt-5 font-serif italic text-azure-soft text-[15px] lg:text-[16.5px] leading-[1.85] tracking-[-0.003em]">
                  The murabbi teaches adab not through a worksheet, but through
                  presence. You are shaping futures — not just minds, but
                  hearts.
                </p>

                <div className="mt-auto pt-8 border-t border-white/10 flex flex-col gap-5">
                  <div className="text-[10.5px] tracking-[0.34em] uppercase text-white/55">
                    Excerpt from{" "}
                    <span className="text-white/80">"The Way of the Murabbi"</span>
                  </div>
                  <Link
                    to={section.viewAllHref}
                    data-testid={`featured-view-all-${section.id}`}
                    className="group self-start inline-flex items-center gap-3 text-[11px] tracking-[0.34em] uppercase text-azure-soft hover:text-azure border-b border-azure-soft/60 hover:border-azure pb-2.5 transition-colors"
                  >
                    Enter the Library
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Featured title beneath the row */}
        <div className="mt-10 lg:mt-12 max-w-2xl">
          <div className="flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-azure-soft">
            <BookOpen className="w-3.5 h-3.5" strokeWidth={1.6} />
            {FEATURED.status}
          </div>
          <h3 className="mt-5 font-serif text-white text-[18px] lg:text-[20px] leading-[1.2] tracking-[-0.01em] max-w-[40ch]">
            {FEATURED.title}
          </h3>
          <div className="mt-6 h-px w-12 bg-azure-soft/60" />
        </div>
      </div>
    </section>
  );
};

export default BooksSection;
